import { bool, number, oneOfType, shape, string } from "prop-types";
import { useCallback } from "react";
import { Link } from "react-router-dom";

import styled from "styled-components/macro";

import { useCanInputGiving } from "Flocknote/stateHooks";

import { DateTime } from "luxon";

import FnrButtonWrapper from "UI/FnrButtonWrapper";
import FnrIcon from "UI/FnrIcon";
import FnrTypography from "UI/FnrTypography";
import { colors, colorsByFunction } from "UI/colors";

import ProgressBar from "./components/ProgressBar";
import Stats from "./components/Stats";

/** @param {Campaign} campaign  */
const Campaign = ({
  ID,
  givingTransactionFundID,
  title,
  startDate,
  endDate,
  goal,
  pledgedAmountGiven,
  unpledgedAmountGiven,
  totalPledge,
  percentOfHouseholdsPledged,
  pledgesCount,
  pledgesFulfilledCount,
  pledgesWithRecurringGiftCount,
  fundColor,
  isParentNetworkCampaign,
  network,
}) => {
  const canInputGiving = useCanInputGiving();

  const onClick = useCallback(() => {
    window.fnReactBus.goToUrl(
      `/giving?fund=${givingTransactionFundID}&campaignID=${ID}&origin=/campaigns`
    );
  }, [ID, givingTransactionFundID]);

  // format to MMM dd
  const start = DateTime.fromISO(startDate).toFormat("MMM dd, yyyy");
  const end = endDate
    ? DateTime.fromISO(endDate).toFormat("MMM dd, yyyy")
    : "Ongoing";

  return (
    <Wrapper>
      <Button onClick={onClick}>
        {isParentNetworkCampaign && network && (
          <ParentNetworkInfo>
            <NetworkPhoto src={network.photoURL} alt={`${network.name} logo`} />
            <NetworkName>{network.name}</NetworkName>
          </ParentNetworkInfo>
        )}
        <TopRow>
          <Text fontSize="1.25rem" style={{ paddingLeft: "0.75rem" }}>
            {title}
          </Text>
          {!isParentNetworkCampaign && !canInputGiving && (
            <SettingsButton
              data-cy="CampaignSettingsButton"
              to={`campaigns/settings/campaign?fund=${givingTransactionFundID}&campaignID=${ID}`}
              onClick={(e) => e.stopPropagation()}
            >
              <FnrIcon icon="fa-cog fas" />
            </SettingsButton>
          )}
        </TopRow>

        <Dates>
          <FnrIcon icon="calendar far" />
          <Text fontSize=".875rem">
            {start} - {end}
          </Text>
        </Dates>

        <ProgressBar
          totalPledge={totalPledge}
          pledgedAmountGiven={pledgedAmountGiven}
          unpledgedAmountGiven={unpledgedAmountGiven}
          goal={goal}
          color={`#${fundColor}`}
        />

        <Stats
          percentOfHouseholdsPledged={percentOfHouseholdsPledged}
          pledgesCount={pledgesCount}
          pledgesFulfilledCount={pledgesFulfilledCount}
          pledgesWithRecurringGiftCount={pledgesWithRecurringGiftCount}
        />
      </Button>
    </Wrapper>
  );
};

Campaign.propTypes = {
  ID: number,
  givingTransactionFundID: number,
  title: string,
  startDate: string,
  endDate: string,
  goal: number,
  pledgedAmountGiven: oneOfType([number, bool]),
  unpledgedAmountGiven: oneOfType([number, bool]),
  totalPledge: oneOfType([number, bool]),
  percentOfHouseholdsPledged: number,
  pledgesCount: number,
  pledgesFulfilledCount: number,
  pledgesWithRecurringGiftCount: number,
  fundColor: string,
  isParentNetworkCampaign: bool,
  network: shape({
    ID: number,
    name: string,
    shortname: string,
    photoURL: string,
  }),
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  color: ${colorsByFunction.textLight};
  background-color: ${colorsByFunction.textAlmostBlack};
  border-radius: 18px;
  box-sizing: border-box;
  container-type: inline-size;
  container-name: campaign;

  * {
    box-sizing: border-box;
  }
`;

const Button = styled(FnrButtonWrapper)`
  padding: 0.75rem;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Text = styled(FnrTypography)`
  color: ${colorsByFunction.textLight};
`;
const Dates = styled(Text)`
  display: flex;
  gap: 0.5rem;
  padding-left: 0.75rem;
`;

const SettingsButton = styled(Link)`
  background: none;
  border: none;
  color: ${colorsByFunction.textLight};
  cursor: pointer;
  border-radius: 0.25rem;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  opacity: 0.7;
  transition: all 0.2s ease;
  rotate: 30deg;

  &:hover {
    background: ${colorsByFunction.textLight};
    opacity: 1;

    & * {
      color: ${colorsByFunction.textPrimary};
    }
  }
`;

const ParentNetworkInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-left: 0.75rem;
  margin-bottom: 2px;
`;

const NetworkPhoto = styled.img`
  width: 18px;
  height: 18px;
  border-radius: 16px;
  object-fit: cover;
  background-color: ${colorsByFunction.textLight};
`;

const NetworkName = styled.div`
  font-size: 0.75rem;
  font-weight: 400;
  color: ${colorsByFunction.textLight};
`;

export default Campaign;
